<template>
  <div class="wizard" v-equalizer="`.wizard__main, .wizard__step`">
    <div class="wizard__progress">
      <span class="wizard__progress__truck" :style="progressStyles"></span>
    </div>
    <div class="row">
      <div class="large-12 columns">
        <section class="wizard__main">
          <main class="wizard__steps">
            <v-step
              step-key="Distance"
            >
              <v-distance
                :header="this.distanceHeader"
                :on-response="this.response"
                :on-previous="this.previous"
              ></v-distance>
            </v-step>

            <v-step
              step-key="Size"
            >
              <v-size
                :header="this.sizeHeader"
                :on-response="this.response"
                :on-previous="this.previous"
              ></v-size>
            </v-step>

            <v-step
              step-key="Packing"
            >
              <v-packing
                :header="this.packingHeader"
                :on-response="this.response"
                :on-previous="this.previous"
              ></v-packing>
            </v-step>

            <v-step
              step-key="Moving Details"
            >
              <v-moving-details
                :header="this.movingDetailsHeader"
                :on-response="this.response"
                :on-previous="this.previous"
              ></v-moving-details>
            </v-step>

            <v-step
              step-key="Local Estimation Boxes Hours"
              :event-label="this.movingDetailsEventLabel"
              :event-value="this.movingDetailsEventValue"
            >
              <v-local-estimation
                :header="this.localEstimationHeading"
                :data="this.data"
                :on-response="this.response"
                :on-previous="this.previous"
              ></v-local-estimation>
            </v-step>

            <v-step
              step-key="Local Estimation Boxes"
              :event-label="this.movingDetailsEventLabel"
              :event-value="this.movingDetailsEventValue"
            >
              <v-local-estimation-boxes
                :header="this.localEstimationBoxesHeading"
                :data="this.data"
                :on-response="this.response"
                :on-previous="this.previous"
              ></v-local-estimation-boxes>
            </v-step>

            <v-step
              step-key="Local Estimation Hours"
              :event-label="this.movingDetailsEventLabel"
              :event-value="this.movingDetailsEventValue"
            >
              <v-local-estimation-hours
                :header="this.localEstimationHoursHeading"
                :data="this.data"
                :on-response="this.response"
                :on-previous="this.previous"
              ></v-local-estimation-hours>
            </v-step>

            <v-step
              step-key="Schedule Walkthrough"
              :event-label="this.movingDetailsEventLabel"
              :event-value="this.movingDetailsEventValue"
            >
              <v-walkthrough
                :header="this.scheduleWalkthroughHeader"
                :data="this.data"
                :on-response="this.response"
                :on-previous="this.previous"
              ></v-walkthrough>
            </v-step>

            <v-step
              step-key="Schedule Move"
            >
              <v-schedule-move
                :header="this.scheduleMoveHeader"
                :on-response="this.response"
                :on-previous="this.previous"
              ></v-schedule-move>
            </v-step>

            <v-step
              step-key="Calculator"
              :event-label="this.movingDetailsEventLabel"
              :event-value="this.movingDetailsEventValue"
            >
              <v-calculator
                :header="this.calculatorHeader"
                :on-response="this.response"
                :on-previous="this.previous"
                :data="this.data"
              ></v-calculator>
            </v-step>

            <v-step
              step-key="Pricing as a Bell-Curve"
              :event-label="this.movingDetailsEventLabel"
              :event-value="this.movingDetailsEventValue"
            >
              <v-bell-curve
                :header="this.prcingAsABellCurveHeader"
                :on-response="this.response"
                :on-previous="this.previous"
                :data="this.data"
              ></v-bell-curve>
            </v-step>

            <v-step step-key="Review"
                :event-label="this.movingDetailsEventLabel"
                :event-value="this.movingDetailsEventValue">
                <v-review
                    :header="this.reviewHeading"
                    :on-response="this.response"
                    :on-previous="this.previous"
                    :data="this.data"
                ></v-review>
            </v-step>

            <v-step
              step-key="Confirmation Call"
              event-label="Schedule on Site Estimate"
              :event-value="270"
            >
              <v-thanks
                :copy="this.confirmationCallThankYouText"
                :data="this.data"
                :on-response="this.response"
                :on-previous="this.previous"
              ></v-thanks>
            </v-step>

            <v-step
              step-key="Call ASAP"
              :event-label="this.movingDetailsEventLabel"
              :event-value="this.movingDetailsEventValue"
            >
              <v-thanks
                :copy="this.callAsapThankYouText"
                :data="this.data"
                :on-response="this.response"
                :on-previous="this.previous"
              ></v-thanks>
            </v-step>

            <v-step
              step-key="Scheduled Thanks"
              event-label="1-2 Bedroom Booking"
              :event-value="191"
            >
              <v-thanks
                :copy="this.scheduledThankYouText"
                :data="this.data"
                :on-response="this.response"
                :on-previous="this.previous"
              ></v-thanks>
            </v-step>

          </main>
          <footer class="wizard__footer">
            <h2 class="wizard__footer__text">Questions or Ready to Schedule? We’re here to help! <a href="tel:2059702222">205.970.2222</a></h2>
          </footer>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
  // promise based http library
  import axios from 'axios';

  import { DateTime } from 'luxon';

  // Checks if the user agent is a mobile device.
  // Use this to check if we should scroll back to the top of the page.
  import isMobile from 'is-mobile';

  // Scrolls page back to the top when wizard changes steps
  // * Mobile Only
  import scrollIntoView from 'dom-scroll-into-view';

  // Tooltip on the calculator screen and on disabled dates.
  import Tooltip from 'tooltip.js';

  // The wizard’s data store that uses local storage.
  // Initially, we were just putting all the values into memory, however,
  // Whit wanted the data to persist even if the user refreshes the page.
  import store from 'store2';

  // Our "router" object. See below
  import BinaryTree from './../classes/BinaryTree';

  export default {
    props: {
       // The plugin controller to send ajax request to once wizard is complete
      action: {
        type: String,
        default: ''
      },

       // If for any reason the wizard should start on a screen that’s not the root node
       // in the binary tree, specify the node number here as an int.
      currentNode: {
        type: Number,
        default: 0
      },

      // The header text on all the different steps
      calculatorHeader: {
        type: String
      },

      callAsapThankYouText: {
        type: String
      },

      confirmationCallThankYouText: {
        type: String
      },

      distanceHeader: {
        type: String
      },

      movingDetailsHeader: {
        type: String
      },

      packingHeader: {
        type: String
      },

      prcingAsABellCurveHeader: {
        type: String
      },

      scheduleMoveHeader: {
        type: String
      },

      scheduleWalkthroughHeader: {
        type: String
      },

      scheduledThankYouText: {
        type: String
      },

      localEstimationHeading: {
        type: String
      },
      localEstimationBoxesHeading: {
        type: String
      },
      localEstimationHoursHeading: {
        type: String
      },
      reviewHeading: {
          type: String
      },

      sizeHeader: {
        type: String
      },

      gclid: {
          type: String,
          default: ''
      },
      nlmCampaign: {
          type: String,
          default: ''
      },
      nlmAdgroup: {
          type: String,
          default: ''
      },

      debug: Boolean

    },

    data: () => ({

      /**
       * Stores the `data` returned from each step
       *
       * 1. Each step above receives the `response` function as a prop
       * 2. The respnse callback gets triggered when the user clicks the "next" button
       * 3. We merge our data store object with the data object received as a paramater
       *
       * @type {object}
       */
      data: store.get('wizard') || {
        path: []
      },

      /**
       * Our binary tree "router" object
       *
       * Each step corresponds to a node and each action an edge.
       * Each node is labeled with the step title, and most edges are labeled with one or more values.
       * When a step returns a value, the adjacent edges are checked and if the returned value equals
       * one of the edge’s values, we traverse to that section of the tree. Some edges do not have a value;
       * in these circumstances that edge is automatically followed regardless of the returned value.
       *
       * See: src/js/classes/BinaryTree
       *
       * @type {BinaryTree}
       */
      router: new BinaryTree(),

      /**
       * Maps the size screen returned value to it's "conversion" value.
       *
       * Used for SEO purposes. This value gets sent to Google/Facebook when the user gets to
       * the moving details screen.
       *
       * Each step can take 2 props:
       * - event-value {Number}
       * - event-label {String}
       * When the step is "active", those two items are sent.
       *
       * @type {Map}
       */
      roomValuesMap: new Map()
        .set('1-2 Br', 24)
        .set('3 Br', 43)
        .set('4 Br', 96)
        .set('5 Br', 160)
        .set('Business', 200),
    }),

    computed: {
      /**
       * Calculates the % progress completed in the wizard
       *
       * Recalculates after each step.
       * Used to update the moving truck progress bar indicator.
       *
       * @return {Number}
       */
      progress() {
        return (this.onStep / this.totalSteps) * 100;
      },

      /**
       * Gets the total # of remaining steps possible based
       * on the current path the user is on.
       *
       * Uses a BST algorithm to find the **LONGEST** remaining path.
       *
       * @return {Number}
       */
      totalSteps() {
        return this.router.getLongestPathLength();
      },

      /**
       * Returns the binary tree depth of our current Node
       *
       * @return {Number}
       */
      onStep() {
        return this.router.getStepNumber();
      },

      /**
       * Calculates the css inline styles for the progress bar.
       *
       * Set the % progress to the real % progress of the wizard.
       * If we get to (or above) 100%, subtract 175px that way the house and moving
       * truck don't overlap.
       *
       * @return {Object}
       */
      progressStyles() {
        return this.progress >= 100 ? {
          width: `calc(100% - 175px)`
        } : {
          width: `${this.progress}%`
        };
      },

      /**
       * Gets the SEO event label that we send when the user gets to the moving details step.
       *
       * Passed as a prop on the moving details step
       *
       * Each step component can take 2 props:
       * - event-value {Number}
       * - event-label {String}
       * When the step is "active", those two items are sent to our SEO class.
       *
       * We get the distance/size from our data store object, and create a string that looks like:
       * - local 1-2 Bedroom Submission
       * - long distance 3 Bedroom Submission
       *
       * @return {String}
       */
      movingDetailsEventLabel() {
        const { distance, size } = this.data;

        return `${(distance || '').toLowerCase()} - ${(size || '').replace('Br', 'Bedroom')} Submission`;
      },

      /**
       * Gets the corresponding value for the event label above using our roomValuesMap
       *
       * Passed as a prop on the moving details step
       *
       * @return {Number}
       */
      movingDetailsEventValue() {
        return this.roomValuesMap.get(this.data.size);
      },


      /**
       * Creates our FormData object for axios
       *
       * @return {FormData}
       */
      formData() {
        const formData = new FormData();
        const data = {
          ...this.data,
          action: this.action,
          path: JSON.stringify(this.router.pathToJson()),
        };

        Object.keys(data).forEach(key => formData.append(key, data[key]));

        return formData;
      }
    },

    /**
     * Vue's main lifecycle hook
     *
     * 1. We initalize all our tooltips using the popper.js library
     * 2. We show the first step of the wizard.
     */
    mounted() {
      // initializes tooltips
      this.initTooltips();
      let localTime = DateTime.local();

      if(this.data.hasOwnProperty('expiryDate')) {
         if(DateTime.local() > DateTime.fromISO(this.data.expiryDate)) {
            store.remove("wizard");
            this.data = { path: [], expiryDate: localTime.plus({months:1}).toISO(), gclid: this.gclid, 'nlm_campaign': this.nlmCampaign, 'nlm_adgroup': this.nlmAdgroup };
            if (this.debug) {
                this.data = { ...this.data, debug:true }
            }
            store.set("wizard", this.data);
         }
       } else {
         // no expiryDate need a clean slate for wizard local storage data
         store.remove("wizard");
         this.data = { path: [], expiryDate: localTime.plus({months:1}).toISO(), gclid: this.gclid, 'nlm_campaign': this.nlmCampaign, 'nlm_adgroup': this.nlmAdgroup };
         if (this.debug) {
                this.data = { ...this.data, debug:true }
          }
         store.set("wizard", this.data);
       }

      // Shows the first step of the wizard according to what's in the data store/currentNode prop
      this.showStep(this.getFirstStep());
    },

    methods: {
      /**
       * Creates all wizard tooltip instances
       *
       * Because the wizard header text is pased in as a prop, we needed a way
       * for the client to create a tooltip using the WYSIWYG.
       *
       * Any tag with the class `.wizard__tooltip` will use the title attribute as a tooltip on hover.
       */
      initTooltips() {
        // get our tooltip instances
        const $tooltips = this.$el.querySelectorAll('.wizard__tooltip');

        // create each one independently
        [...$tooltips].forEach($tooltip => {
          const toolTipInstance = new Tooltip($tooltip, {
            placement: 'top'
          });

          // dismiss each tooltip when the user clicks the screen
          //
          // **bug on mobile fix**
          document.body.addEventListener('touchstart', $event => {
            toolTipInstance.hide();
          });
        });
      },

      /**
       * Makes a step active on the wizard based on key.
       *
       * Each v-step component has a **unique** key prop.
       *
       * @param  {String}  key  key of active step
       */
      showStep(key) {
        // loop through all of children to find the step with the correct key
        const step = this.$children.find($child => $child.stepKey == key);

        // hide all old step, show new one
        this.$children.forEach($child => $child.active = false);
        step.active = true;
      },

      /**
       * The response callback sent to each step as a prop.
       *
       * 1. Merge our data objects
       * 2. Stores path
       * 3. Traverses to the next node
       * 3. Checks to see if the wizard is complete yet, if so, send our ajax request.
       * 4. Scrolls back to the top on mobile
       *
       * @param  {Object}  options.data      What should be merged into our data store object
       * @param  {String}  options.response  What key should be used to find the next step. This key is checked with all adjacent edge weights
       */
      response({ data, response }) {
        // merge our data objects
        this.data = {
          ...this.data,
          ...data,
          ...{ gclid: this.gclid, 'nlm_campaign': this.nlmCampaign, 'nlm_adgroup': this.nlmAdgroup }
        };

        // save the path we took to get here.
        // it's userful to see in the backend.
        this.data.path.push({
          step: this.router.current.value,
          value: response
        });

        // call our router traverse function.
        // 1. Each step returns a response.
        // 2. All the adjacent edges on our current node gets checked
        // 3. If the response === the edge weight, we traverse to that node.
        // **If a node has an edge with no weight, it is automatically followed
        const nextNode = this.router.traverse(response);

        // send the next node value (step key) to our showStep function
        this.showStep(nextNode.value);

        // store all the data we have into local storage
        store.set('wizard', this.data);

        // do our ajax request!
        // if (this.progress >= 100) {
        //   this.sendData();
        // }

        if((this.router.getStepNumber() >= 4)) {
              this.sendData();
          }

        // scroll to the top on mobile
        if (isMobile() && this.progress > 0) {
          scrollIntoView(this.$el, document, {
            alignWithLeft: true,
            alignWithTop: true,
            onlyScrollIfNeeded: true,
            offsetTop: -75
          });
        }
      },

      /**
       * Goes back to the previous screen
       *
       * 1. We retreat our router one step
       * 2. We pop the latest step from our path array
       * 3. Show the previous step
       */
      previous() {
        // get the previous node
        const previousNode = this.router.retreat();

        // pop the last step from our path array (we don't need it)
        this.data.path.pop();

        // show the previous node
        this.showStep(previousNode.value);
      },

      /**
       * 1. Sends our ajax request to the server.
       * 2. Merges error or confirmation # to the data object after.
       * 3. Deletes wizard store from local storage.
       */
      async sendData() {
        let response = {};
        if(this.debug === false) {
            // make our request
            response = await axios.post('/', this.formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              responseType: 'json'
            });

        } else {
            response = { "data": { "confirmationNumber": "0000000" } };
        }

        let progress = { progress: Math.round(this.progress) };
        // merge our data object
        this.data = { ...this.data, ...response.data, ...progress };

        // removes wizard store from local storage
        // that way the user can complete the wizard again
        // if ever so desired.
        if(this.debug === false) {
            if (this.progress >= 100) {
                store.remove("wizard");
            }
        }
      },

      /**
       * Determines which step to show first:
       *
       * 1. If the `current-node` prop is defined and is a Number, the wizard will begin at whichever screen the node corresponds to.
       * 2. If we have saved information in the data store already, we check which screen should be shown next by traversing all the saved
       * responses through the wizard.
       * 3. If none of that is defined, we start from the beginning 😸
       *
       * @return {String} the key of which step to show next
       */
      getFirstStep() {
        this.router.setCurrentNode(this.currentNode);

        this.data.path.forEach(response => this.router.traverse(response.value));

        return this.router.current.value;
      }
    }
  };
</script>
